import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export const Container = ({ className = '', children }) => (
  <div className={`max-w-[1180px] mx-auto px-5 sm:px-8 ${className}`}>{children}</div>
);

// Six-point outline star used before every section label.
export const StarMark = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M12 2.5 20.5 17H3.5L12 2.5Z" />
    <path d="M12 21.5 3.5 7h17L12 21.5Z" />
  </svg>
);

export const Eyebrow = ({ children, dark = false, className = '', starClassName }) => (
  <div className={`inline-flex items-center gap-2 text-sm ${dark ? 'text-white/90' : 'text-[#111]/80'} ${className}`}>
    <StarMark className={`w-4 h-4 ${starClassName || (dark ? 'text-[#6d84ff]' : 'text-[#3b5bfd]')}`} />
    <span>{children}</span>
  </div>
);

const variants = {
  dark: 'bg-[#111] text-white',
  light: 'bg-white text-[#111]',
  glass: 'bg-white/10 text-white border border-white/10',
};

// Pill label + detached round arrow, the reference's signature button.
export const PillButton = ({ to, href, type, onClick, disabled, children, variant = 'dark', size = 'md', block = false, className = '' }) => {
  const tone = variants[variant];
  const h = size === 'sm' ? 'h-10' : 'h-11';
  const inner = (
    <>
      <span className={`${h} ${block ? 'flex-1 justify-center' : ''} px-7 inline-flex items-center rounded-full text-sm font-medium whitespace-nowrap ${tone}`}>
        {children}
      </span>
      <span className={`${h} aspect-square -ml-px rounded-full inline-flex items-center justify-center shrink-0 ${tone}`}>
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-45" />
      </span>
    </>
  );
  const cls = `group ${block ? 'flex w-full' : 'inline-flex'} items-center transition-transform duration-200 hover:-translate-y-0.5 ${className}`;

  if (type) {
    return (
      <button type={type} onClick={onClick} disabled={disabled} className={`${cls} cursor-pointer disabled:opacity-60 disabled:pointer-events-none`}>
        {inner}
      </button>
    );
  }
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  // No target: visual only, for use inside an element that is already a link.
  if (!to) {
    return <span className={cls}>{inner}</span>;
  }
  return (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
};

// Light ↔ navy blends. A plain two-stop linear-gradient shows a visible "edge"
// where it starts and stops, so these ease in and out (smoothstep) instead.
export const LIGHT = '#e9ecf2';
export const NAVY = '#0b2471';

const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

export const easedGradient = (from, to, start = 0, end = 100, steps = 16) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const stops = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const e = t * t * (3 - 2 * t);
    const c = a.map((v, k) => Math.round(v + (b[k] - v) * e));
    stops.push(`rgb(${c.join(',')}) ${(start + (end - start) * t).toFixed(2)}%`);
  }
  return `linear-gradient(180deg, ${stops.join(', ')})`;
};

// Navy section whose top blends in from the light page background. The blend height
// (--navy-fade) and top padding are shorter on phones so the gap above the heading stays tight.
export const navyIn = {
  backgroundColor: NAVY,
  backgroundImage: easedGradient(LIGHT, NAVY),
  backgroundSize: '100% var(--navy-fade, 360px)',
  backgroundRepeat: 'no-repeat',
};

export const NAVY_SECTION = 'pt-32 lg:pt-40 pb-24 [--navy-fade:190px] lg:[--navy-fade:260px]';

export const Fade = ({ from, to, className = 'h-48' }) => (
  <div aria-hidden="true" className={className} style={{ background: easedGradient(from, to) }} />
);
